/**
 * UserForm Component
 *
 * Form for creating and editing users with Formik + Yup validation.
 * Features:
 * - Create new user or edit existing user
 * - Client selector (APP_ADMIN only, create mode only)
 * - userName, email, firstName, lastName, groupRole, group, employeeId fields
 * - Role dropdown (conditional - only visible for grm client)
 * - Form validation with real-time feedback
 * - Submission with Cognito integration and GraphQL mutations
 */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import AdminButton from '../../../../../components/common/AdminButton';

// Services
import { createUser, updateUser } from '../services/userService';
import { listRoles, formatRolesForDropdown } from '../services/roleService';
import { listClients } from '../services/clientService';

// Utils
import { getEmailError, getFirstNameError, getLastNameError } from '../utils/userValidation';
import { shouldShowRoleField, getRoleError, sanitizeRole } from '../utils/roleHelpers';

// Constants
import {
  GROUP_ROLE_OPTIONS,
  GROUP_OPTIONS,
  ORG_LEVEL_OPTIONS,
  USER_STATUS_OPTIONS,
  VALIDATION,
  MESSAGES,
  GROUP_ROLES,
} from '../constants';

/**
 * UserForm Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {function} props.onSuccess - Callback when user is created/updated successfully
 * @param {string} props.clientId - The client ID for the user (CLIENT_ADMIN)
 * @param {Object} [props.user] - User object for edit mode (null for create mode)
 * @param {Object} [props.currentUser] - Currently logged in user (to prevent self-edit and determine APP_ADMIN)
 */
export default function UserForm({ open, onClose, onSuccess, clientId, user = null, currentUser = null }) {
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(clientId || '');
  const [submitting, setSubmitting] = useState(false);

  const isEditMode = Boolean(user);
  const isOwnAccount = isEditMode && currentUser && user.id === currentUser.id;
  const isAppAdmin = currentUser?.groupRole === GROUP_ROLES.APP_ADMIN;
  const showClientSelector = isAppAdmin && !isEditMode;
  const effectiveClientId = showClientSelector ? selectedClient : clientId;
  const showRoleField = shouldShowRoleField(effectiveClientId);

  // Filter Group Role options - only show APP_ADMIN when user is APP_ADMIN and client is RadixDash
  const filteredGroupRoleOptions = useMemo(() => {
    return GROUP_ROLE_OPTIONS.filter(option => {
      // APP_ADMIN option should only appear when:
      // 1. Current user is APP_ADMIN
      // 2. Selected client is RadixDash
      if (option.value === GROUP_ROLES.APP_ADMIN) {
        return isAppAdmin && effectiveClientId === 'RadixDash';
      }
      // All other roles are always available
      return true;
    });
  }, [isAppAdmin, effectiveClientId]);

  // Fetch clients for APP_ADMIN (create mode only)
  useEffect(() => {
    if (open && showClientSelector) {
      fetchClients();
    }
  }, [open, showClientSelector]);

  // Fetch roles for grm client
  useEffect(() => {
    if (open && showRoleField && effectiveClientId) {
      fetchRoles();
    }
  }, [open, showRoleField, effectiveClientId]);

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const fetchedClients = await listClients();
      setClients(fetchedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      enqueueSnackbar('Failed to load clients', { variant: 'error' });
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchRoles = async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const fetchedRoles = await listRoles(effectiveClientId);
      setRoles(formatRolesForDropdown(fetchedRoles));
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRolesError('Failed to load roles');
      enqueueSnackbar('Failed to load roles', { variant: 'error' });
    } finally {
      setRolesLoading(false);
    }
  };

  // Form initial values
  const initialValues = {
    userName: user?.userName || '',
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    groupRole: user?.groupRole || '',
    group: user?.group || '',
    employeeId: user?.employeeId || '',
    role: user?.role || '',
    status: user?.status ?? (isEditMode ? '' : 'ACTIVE'), // Preserve null/undefined in edit mode, default to ACTIVE for new users
  };

  // Yup validation schema
  const validationSchema = Yup.object().shape({
    userName: Yup.string()
      .min(VALIDATION.USERNAME_MIN_LENGTH, `Username must be at least ${VALIDATION.USERNAME_MIN_LENGTH} characters`)
      .max(VALIDATION.USERNAME_MAX_LENGTH, `Username must be ${VALIDATION.USERNAME_MAX_LENGTH} characters or less`)
      .required('Username is required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .max(VALIDATION.EMAIL_MAX_LENGTH, `Email must be ${VALIDATION.EMAIL_MAX_LENGTH} characters or less`)
      .required('Email is required')
      .test('custom-email-validation', function (value) {
        const error = getEmailError(value);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    firstName: Yup.string()
      .min(VALIDATION.FIRST_NAME_MIN_LENGTH, `First name must be at least ${VALIDATION.FIRST_NAME_MIN_LENGTH} character`)
      .max(VALIDATION.FIRST_NAME_MAX_LENGTH, `First name must be ${VALIDATION.FIRST_NAME_MAX_LENGTH} characters or less`)
      .required('First name is required')
      .test('custom-first-name-validation', function (value) {
        const error = getFirstNameError(value);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    lastName: Yup.string()
      .min(VALIDATION.LAST_NAME_MIN_LENGTH, `Last name must be at least ${VALIDATION.LAST_NAME_MIN_LENGTH} character`)
      .max(VALIDATION.LAST_NAME_MAX_LENGTH, `Last name must be ${VALIDATION.LAST_NAME_MAX_LENGTH} characters or less`)
      .required('Last name is required')
      .test('custom-last-name-validation', function (value) {
        const error = getLastNameError(value);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    groupRole: Yup.string()
      .required('Group role is required'),
    group: Yup.string()
      .nullable(),
    employeeId: Yup.string()
      .max(VALIDATION.EMPLOYEE_ID_MAX_LENGTH, `Employee ID must be ${VALIDATION.EMPLOYEE_ID_MAX_LENGTH} characters or less`)
      .nullable(),
    role: Yup.string()
      .test('conditional-role-validation', function (value) {
        const error = getRoleError(effectiveClientId, value);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    status: Yup.string()
      .test('status-required', 'Status is required', function(value) {
        // Allow empty string only if it's the initial value and form is not dirty
        if (!value || value === '') {
          return false;
        }
        return true;
      })
      .oneOf(['ACTIVE', 'SUSPENDED', 'DELETED', ''], 'Invalid status'),
  });

  // Handle form submission
  const handleSubmit = async (values, { setFieldError }) => {
    // Prevent self-edit
    if (isOwnAccount) {
      enqueueSnackbar(MESSAGES.CANNOT_EDIT_SELF, { variant: 'warning' });
      return;
    }

    // Validate client selection for APP_ADMIN create mode
    if (showClientSelector && !effectiveClientId) {
      enqueueSnackbar('Please select a client', { variant: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      const sanitizedRole = sanitizeRole(effectiveClientId, values.role);

      const userData = {
        userName: values.userName.trim(),
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        groupRole: values.groupRole,
        status: values.status,
        group: values.group || undefined,
        employeeId: values.employeeId?.trim() || undefined,
        role: sanitizedRole,
      };

      if (isEditMode) {
        // Update existing user
        await updateUser({
          id: user.id,
          ...userData,
        });

        enqueueSnackbar(MESSAGES.USER_UPDATED_SUCCESS, { variant: 'success' });
      } else {
        // Create new user
        await createUser({
          client: effectiveClientId,
          ...userData,
        });

        enqueueSnackbar(MESSAGES.USER_CREATED_SUCCESS, { variant: 'success' });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);

      // Handle specific error cases
      if (error.message?.includes('already exists') || error.message?.includes('duplicate')) {
        setFieldError('email', MESSAGES.EMAIL_ALREADY_EXISTS);
        enqueueSnackbar(MESSAGES.EMAIL_ALREADY_EXISTS, { variant: 'error' });
      } else if (error.message?.includes('network') || error.message?.includes('Network')) {
        enqueueSnackbar(MESSAGES.NETWORK_ERROR, { variant: 'error' });
      } else if (error.message?.includes('permission') || error.message?.includes('unauthorized')) {
        enqueueSnackbar(MESSAGES.PERMISSION_DENIED, { variant: 'error' });
      } else {
        const errorMessage = isEditMode ? MESSAGES.USER_UPDATE_ERROR : MESSAGES.USER_CREATE_ERROR;
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle client selection
  const handleClientChange = (event, newValue) => {
    setSelectedClient(newValue?.clientId || '');
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditMode ? 'Edit User' : 'Create New User'}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, values, handleChange, handleBlur, isValid, dirty }) => (
          <Form>
            <DialogContent>
              {isOwnAccount && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {MESSAGES.CANNOT_EDIT_SELF}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Client Selector (APP_ADMIN only, create mode only) */}
                {showClientSelector && (
                  <Box>
                    <Autocomplete
                      options={clients}
                      getOptionLabel={(option) => option.clientName || option.clientId || ''}
                      value={clients.find(c => c.clientId === selectedClient) || null}
                      onChange={handleClientChange}
                      loading={clientsLoading}
                      disabled={submitting}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client *"
                          placeholder="Select a client..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {clientsLoading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {!effectiveClientId && (
                      <FormHelperText error>Client is required</FormHelperText>
                    )}
                  </Box>
                )}

                {/* Username Field */}
                <TextField
                  name="userName"
                  label="Username *"
                  value={values.userName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userName && Boolean(errors.userName)}
                  helperText={touched.userName && errors.userName}
                  disabled={isEditMode || submitting}
                  fullWidth
                />

                {/* Email Field */}
                <TextField
                  name="email"
                  label="Email *"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  disabled={isOwnAccount || submitting}
                  fullWidth
                />

                {/* First Name Field */}
                <TextField
                  name="firstName"
                  label="First Name *"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  disabled={isOwnAccount || submitting}
                  fullWidth
                />

                {/* Last Name Field */}
                <TextField
                  name="lastName"
                  label="Last Name *"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  disabled={isOwnAccount || submitting}
                  fullWidth
                />

                {/* Group Role Field */}
                <FormControl
                  fullWidth
                  error={touched.groupRole && Boolean(errors.groupRole)}
                  disabled={isOwnAccount || submitting}
                >
                  <InputLabel>Group Role *</InputLabel>
                  <Select
                    name="groupRole"
                    value={values.groupRole}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Group Role *"
                  >
                    {filteredGroupRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.groupRole && errors.groupRole && (
                    <FormHelperText>{errors.groupRole}</FormHelperText>
                  )}
                </FormControl>

                {/* Status Field */}
                <FormControl
                  fullWidth
                  error={touched.status && Boolean(errors.status)}
                  disabled={isOwnAccount || submitting}
                >
                  <InputLabel>Status *</InputLabel>
                  <Select
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Status *"
                  >
                    {values.status === '' && (
                      <MenuItem value="" disabled>
                        <em>Select a status...</em>
                      </MenuItem>
                    )}
                    {USER_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.status && errors.status && (
                    <FormHelperText>{errors.status}</FormHelperText>
                  )}
                </FormControl>

                {/* Group (Department) Field */}
                <FormControl
                  fullWidth
                  error={touched.group && Boolean(errors.group)}
                  disabled={isOwnAccount || submitting}
                >
                  <InputLabel>Group (Department)</InputLabel>
                  <Select
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Group (Department)"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {GROUP_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.group && errors.group && (
                    <FormHelperText>{errors.group}</FormHelperText>
                  )}
                </FormControl>

                {/* Employee ID Field */}
                <TextField
                  name="employeeId"
                  label="Employee ID"
                  value={values.employeeId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.employeeId && Boolean(errors.employeeId)}
                  helperText={touched.employeeId && errors.employeeId}
                  disabled={isOwnAccount || submitting}
                  fullWidth
                />

                {/* Role Field (Conditional - only for grm client) */}
                {showRoleField && (
                  <>
                    {rolesError && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {rolesError}
                      </Alert>
                    )}

                    <FormControl
                      fullWidth
                      error={touched.role && Boolean(errors.role)}
                      disabled={isOwnAccount || submitting || rolesLoading}
                    >
                      <InputLabel>Role *</InputLabel>
                      <Select
                        name="role"
                        value={values.role}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Role *"
                      >
                        {rolesLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading roles...
                          </MenuItem>
                        ) : roles.length === 0 ? (
                          <MenuItem disabled>No roles available</MenuItem>
                        ) : (
                          roles.map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                              {role.label}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {touched.role && errors.role && (
                        <FormHelperText>{errors.role}</FormHelperText>
                      )}
                    </FormControl>
                  </>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <AdminButton onClick={onClose} disabled={submitting} variant="outlined">
                Cancel
              </AdminButton>
              <AdminButton
                type="submit"
                variant="contained"
                disabled={
                  isOwnAccount ||
                  submitting ||
                  !isValid ||
                  !dirty ||
                  (showClientSelector && !effectiveClientId)
                }
              >
                {submitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update User' : 'Create User'
                )}
              </AdminButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

UserForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  clientId: PropTypes.string,
  user: PropTypes.object,
  currentUser: PropTypes.object,
};
